import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, Pencil, Plus, Search, Trash2, UserPlus, Users, X } from 'lucide-react';
import { listUserProfilesByName } from '../../../database/userProfiles';
import {
  createPlayer,
  removePlayerFromTournament,
  subscribePlayers,
  updatePlayer,
} from '../../../database/tournamentPlayers';
import { addStageParticipants, subscribeStages } from '../../../database/tournamentStages';
import { canWithdrawFrom } from '../../../lib/stages';
import {
  ENTRY_NAME_MAX,
  entryByline,
  entryFields,
  entryMembers,
  validateEntry,
} from '../../../lib/entries';
import { handleFirebaseError } from '../../../utils/errorHandler';
import { cn } from '../../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import ConfirmDialog from '../common/ConfirmDialog';

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </div>
);

const memberFromUser = (u) => ({
  userId: u.id,
  name: u.username || '',
  imageUrl: u.profilePicUrl || '',
  country: u.location || '',
  countryName: u.countryName || '',
});

const defaultEntryName = (members) =>
  members.length <= 1
    ? members[0]?.name || ''
    : `${members
        .slice(0, -1)
        .map((m) => m.name)
        .join(', ')} & ${members[members.length - 1].name}`;

// Searchable user list. Picks toggle in and out of `selectedIds`.
const UserPicker = ({ users, selectedIds, takenIds, onToggle }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return users;
    return users.filter(
      (u) => u.username?.toLowerCase().includes(s) || u.countryName?.toLowerCase().includes(s)
    );
  }, [users, search]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by username or country"
          className="pl-9"
        />
      </div>
      <div className="max-h-56 overflow-y-auto rounded-md border border-border/60">
        {filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">No users match.</div>
        ) : (
          <ul className="divide-y divide-border/60">
            {filtered.map((u) => {
              const taken = takenIds.has(u.id);
              const selected = selectedIds.includes(u.id);
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    disabled={taken}
                    aria-pressed={selected}
                    onClick={() => onToggle(u)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                      taken && 'cursor-not-allowed opacity-40',
                      selected ? 'bg-secondary text-foreground' : 'hover:bg-secondary/60'
                    )}
                  >
                    <Avatar className="h-7 w-7">
                      {u.profilePicUrl && <AvatarImage src={u.profilePicUrl} alt="" />}
                      <AvatarFallback>{initials(u.username)}</AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1 truncate">{u.username || '—'}</span>
                    {u.countryName && (
                      <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground sm:inline">
                        {u.countryName}
                      </span>
                    )}
                    {taken && <Badge variant="outline">In roster</Badge>}
                    {selected && <Check className="h-4 w-4 shrink-0 text-heat" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// Chips for the people behind an entry, each removable.
const MemberChips = ({ members, onRemove }) => (
  <ul className="flex flex-wrap gap-2">
    {members.map((m) => (
      <li
        key={m.userId}
        className="inline-flex items-center gap-1.5 rounded-sm border border-border/60 py-1 pl-2 pr-1 text-sm"
      >
        {m.name}
        <button
          type="button"
          onClick={() => onRemove(m.userId)}
          aria-label={`Remove ${m.name}`}
          className="rounded-sm p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </li>
    ))}
  </ul>
);

// Create or edit one entry: its name and the people behind it.
const EntryDialog = ({ open, onOpenChange, entry, users, takenIds, onSave, liveStage }) => {
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [members, setMembers] = useState([]);
  const [joinLive, setJoinLive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(entry?.name || '');
    setNameTouched(!!entry);
    setMembers(entry ? entryMembers(entry) : []);
    setJoinLive(true);
  }, [open, entry]);

  const toggle = (u) =>
    setMembers((list) => {
      const next = list.some((m) => m.userId === u.id)
        ? list.filter((m) => m.userId !== u.id)
        : [...list, memberFromUser(u)];
      if (!nameTouched) setName(defaultEntryName(next));
      return next;
    });

  const remove = (userId) =>
    setMembers((list) => {
      const next = list.filter((m) => m.userId !== userId);
      if (!nameTouched) setName(defaultEntryName(next));
      return next;
    });

  const errors = validateEntry(name, members);

  const handleSave = async () => {
    setBusy(true);
    try {
      await onSave(entryFields(name, members), { joinLive: !entry && liveStage && joinLive });
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not save the entry.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  // People in this entry stay pickable; people in other entries don't.
  const ownIds = new Set(entry ? entryMembers(entry).map((m) => m.userId) : []);
  const pickerTaken = new Set([...takenIds].filter((id) => !ownIds.has(id)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{entry ? `Edit ${entry.name}` : 'Add to roster'}</DialogTitle>
        </DialogHeader>

        <Field
          label="People"
          hint="Pick one player, or several people who made one entry together."
        >
          {members.length > 0 && <MemberChips members={members} onRemove={remove} />}
          <UserPicker
            users={users}
            selectedIds={members.map((m) => m.userId)}
            takenIds={pickerTaken}
            onToggle={toggle}
          />
        </Field>

        <Field
          label="Shown as"
          hint="The name everyone sees on leaderboards, votes and the stream. For a mapping contest use the map name. Leave as is to use the player's name."
        >
          <Input
            value={name}
            maxLength={ENTRY_NAME_MAX}
            placeholder="e.g. kz_frostbite"
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
            }}
          />
        </Field>

        {!entry && liveStage && (
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-heat"
              checked={joinLive}
              onChange={(e) => setJoinLive(e.target.checked)}
            />
            <span>
              Also add to <span className="font-medium">{liveStage.name}</span>, which is already
              live.
            </span>
          </label>
        )}

        {members.length > 0 && errors.length > 0 && (
          <ul className="list-inside list-disc text-sm text-destructive">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={busy || errors.length > 0}>
            {busy ? 'Saving…' : entry ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RosterRow = ({ player, onEdit, onRemove }) => {
  const byline = entryByline(player);
  const members = entryMembers(player);
  return (
    <li className="flex items-center gap-3 px-1 py-3">
      <Avatar className="h-10 w-10 shrink-0">
        {player.imageUrl && <AvatarImage src={player.imageUrl} alt="" />}
        <AvatarFallback>
          {members.length > 1 ? <Users className="h-4 w-4" /> : initials(player.name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-base font-medium">{player.name}</div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {byline ? `by ${byline}` : player.countryName || ' '}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Edit ${player.name}`}>
        <Pencil className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Edit</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive hover:text-destructive"
        onClick={onRemove}
        aria-label={`Remove ${player.name}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </li>
  );
};

// The roster of a staged tournament. Each entry is one player or a group of
// people (e.g. a mapping team) shown under one name.
const RosterManager = ({ tournamentId }) => {
  const [users, setUsers] = useState([]);
  const [players, setPlayers] = useState(null);
  const [stages, setStages] = useState([]);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [toRemove, setToRemove] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listUserProfilesByName()
      .then(setUsers)
      .catch((err) =>
        toast.error('Could not load users.', { description: handleFirebaseError(err) })
      );
  }, []);

  useEffect(() => {
    setPlayers(null);
    setError(null);
    const unsubPlayers = subscribePlayers(tournamentId, setPlayers, (err) =>
      setError(handleFirebaseError(err))
    );
    const unsubStages = subscribeStages(tournamentId, setStages, () => {});
    return () => {
      unsubPlayers();
      unsubStages();
    };
  }, [tournamentId]);

  const takenIds = useMemo(
    () => new Set((players || []).flatMap((p) => entryMembers(p).map((m) => m.userId))),
    [players]
  );

  // Late entries can join a running first-stage leaderboard.
  const liveStage =
    stages[0]?.status === 'active' && stages[0].type === 'leaderboard' ? stages[0] : null;

  const saveEntry = async (fields, { joinLive }) => {
    if (dialog?.entry) {
      await updatePlayer(tournamentId, dialog.entry.id, fields);
      toast.success(`${fields.name} saved.`);
      return;
    }
    const ref = await createPlayer(tournamentId, fields);
    if (joinLive) await addStageParticipants(tournamentId, liveStage.id, [ref.id]);
    toast.success(`${fields.name} added${joinLive ? ` to ${liveStage.name}` : ''}.`);
  };

  const confirmRemove = async () => {
    setBusy(true);
    try {
      await removePlayerFromTournament(tournamentId, toRemove.id, stages);
      toast.success(`${toRemove.name} removed.`);
      setToRemove(null);
    } catch (err) {
      toast.error('Could not remove.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  const removeStages = toRemove
    ? stages.filter((s) => (s.participants || []).includes(toRemove.id))
    : [];
  const removeBlocked = removeStages.find((s) => !canWithdrawFrom(s, toRemove?.id));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {players ? `${players.length} ${players.length === 1 ? 'entry' : 'entries'}` : 'Loading…'}
        </p>
        <Button onClick={() => setDialog({ entry: null })} className="shrink-0">
          <UserPlus className="h-4 w-4" />
          Add to roster
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 px-6 py-8 text-center text-sm text-destructive">
          {error}
        </div>
      ) : !players ? (
        <div className="divide-y divide-border/60 border-y border-border/60">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex h-16 animate-pulse items-center gap-4 px-2">
              <div className="h-10 w-10 rounded-full bg-muted" />
              <div className="h-4 w-40 bg-muted" />
            </div>
          ))}
        </div>
      ) : players.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
          Nobody yet.{' '}
          <button
            type="button"
            className="text-heat underline-offset-2 hover:underline"
            onClick={() => setDialog({ entry: null })}
          >
            <Plus className="mr-1 inline h-3.5 w-3.5" />
            Add the first entry
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-border/60 border-y border-border/60">
          {players.map((p) => (
            <RosterRow
              key={p.id}
              player={p}
              onEdit={() => setDialog({ entry: p })}
              onRemove={() => setToRemove(p)}
            />
          ))}
        </ul>
      )}

      <EntryDialog
        open={!!dialog}
        onOpenChange={(o) => !o && setDialog(null)}
        entry={dialog?.entry || null}
        users={users}
        takenIds={takenIds}
        liveStage={liveStage}
        onSave={saveEntry}
      />

      <ConfirmDialog
        open={!!toRemove}
        title={`Remove ${toRemove?.name}?`}
        description={
          removeBlocked
            ? `${toRemove?.name} plays in the ${removeBlocked.name} bracket, which has started. Reset that stage first to remove them.`
            : removeStages.length
              ? `They are taken out of ${removeStages.map((s) => s.name).join(', ')} too, with their scores. Their community votes stop counting. This can't be undone.`
              : 'They are taken off the roster. This can’t be undone.'
        }
        confirmLabel="Remove"
        destructive
        busy={busy}
        confirmDisabled={!!removeBlocked}
        onOpenChange={(o) => !o && setToRemove(null)}
        onConfirm={confirmRemove}
      />
    </div>
  );
};

export default RosterManager;
